import {
  EnvironmentId,
  ThreadId,
  type CommandId,
  type EnvironmentApi,
} from "@cmmd-forge/contracts";
import { scopeThreadRef } from "@cmmd-forge/client-runtime";

import { readCmmdForgeRuntimeListSnapshot } from "../cmmd/cmmdForgeRuntimeListResource";
import { useCloudRuntimeProjectHandoffStore } from "../components/sidebar/cloudRuntimeProjectHandoffStore";
import { readEnvironmentApi } from "../environmentApi";
import { classifyEnvironmentId } from "../environments/environmentIdClassification";
import { readPrimaryEnvironmentDescriptor } from "../environments/primary";
import { getSavedEnvironmentRecord } from "../environments/runtime/catalog";
import { addConnectedSavedEnvironment } from "../environments/runtime/service";
import { resolveForgeCloudEnvironmentLabel } from "../environments/forgeCloudEnvironmentLabel";
import {
  prepareRuntimeExecutionIntent,
  type RuntimeExecutionConnection,
  type RuntimeExecutionIntentAction,
  type RuntimeExecutionIntentData,
  type RuntimeExecutionIntentWaitHandler,
} from "../environments/runtime/runtimeExecutionIntentApi";
import { findCloudRowForEnvironment } from "../routes/cloudRouteCatalogRow";
import { findCloudEnvironmentLifecycleState } from "../routes/ensureActiveRouteCloudRuntime";
import { ensureDesktopRuntimeEnvironmentApi } from "./ensureDesktopRuntimeEnvironmentApi";
import { readThreadCmmdProjectId } from "./threadCmmdProjectId";

const THREAD_RUNTIME_UNAVAILABLE_MESSAGE = "The thread's runtime is unavailable for settlement.";

export interface ThreadActionEnvironmentApiDeps {
  readonly readApi: (environmentId: EnvironmentId) => EnvironmentApi | undefined;
  readonly isCloudEnvironment: (environmentId: EnvironmentId) => boolean;
  readonly connectDesktopEnvironment: typeof ensureDesktopRuntimeEnvironmentApi;
  readonly readPrimaryEnvironmentId: () => EnvironmentId | null;
  readonly readEnvironmentLabel: (environmentId: EnvironmentId) => string;
  /** Resolves compatibility locators to CMMD's durable Environment key. */
  readonly resolveCloudEnvironmentId: (environmentId: EnvironmentId) => EnvironmentId | null;
  /** A paused Environment must never receive an automatic wake request. */
  readonly readCloudLifecycleState: (environmentId: EnvironmentId) => string | undefined;
}

const PAUSED_ENVIRONMENT_MESSAGE = "This Environment is paused. Resume it before live work.";
const UNAVAILABLE_ENVIRONMENT_MESSAGE =
  "This Environment is not accepting live work while it drains.";
const MISSING_CHECKOUT_REGISTRATION_MESSAGE =
  "This Checkout is missing its CMMD project registration.";

function findCloudCatalogRow(environmentId: EnvironmentId) {
  const catalogRows = readCmmdForgeRuntimeListSnapshot().rows;
  return findCloudRowForEnvironment(
    environmentId,
    catalogRows,
    Object.values(useCloudRuntimeProjectHandoffStore.getState().byRuntimeId),
  );
}

const DEFAULT_DEPS: ThreadActionEnvironmentApiDeps = {
  readApi: readEnvironmentApi,
  isCloudEnvironment: (environmentId) => classifyEnvironmentId(environmentId).isCloud,
  connectDesktopEnvironment: ensureDesktopRuntimeEnvironmentApi,
  readPrimaryEnvironmentId: () => readPrimaryEnvironmentDescriptor()?.environmentId ?? null,
  readEnvironmentLabel: (environmentId) =>
    getSavedEnvironmentRecord(environmentId)?.label ?? String(environmentId),
  resolveCloudEnvironmentId: (environmentId) =>
    (findCloudCatalogRow(environmentId)?.row.cmmdEnvironmentId ?? null) as EnvironmentId | null,
  readCloudLifecycleState: (environmentId) => {
    const snapshot = readCmmdForgeRuntimeListSnapshot();
    return findCloudEnvironmentLifecycleState(environmentId, snapshot.environments);
  },
};

export interface ThreadExecutionIntentEnvironmentApiDeps extends ThreadActionEnvironmentApiDeps {
  readonly prepareRuntimeExecutionIntent: typeof prepareRuntimeExecutionIntent;
  /**
   * Names the Registration of a thread whose Checkout carries no stamp, the way a retained
   * thread recovers it. Callers pass the stamp they hold, and a cold Checkout holds none.
   */
  readonly recoverCheckoutRegistration: (
    environmentId: EnvironmentId,
    threadId: string,
  ) => number | null;
  readonly registerRuntimeExecutionConnection: (input: {
    readonly environmentId: EnvironmentId;
    readonly connection: RuntimeExecutionConnection;
    readonly signal?: AbortSignal;
  }) => Promise<void>;
}

/**
 * Saves the Environment an Execution Intent connected, under the label its
 * Organization gives it, so a send can reach it.
 */
async function registerRuntimeExecutionConnection(input: {
  readonly environmentId: EnvironmentId;
  readonly connection: RuntimeExecutionConnection;
  readonly signal?: AbortSignal;
}): Promise<void> {
  const resolvedEnvironmentId = EnvironmentId.make(
    input.connection.runtime.environmentId ?? input.environmentId,
  );
  await addConnectedSavedEnvironment({
    environment: {
      environmentId: resolvedEnvironmentId,
      label: resolveForgeCloudEnvironmentLabel(input.connection.runtime.organizationId),
      kind: "project",
      runtimeId: input.connection.runtime.runtimeId,
      cmmdOrganizationId: String(input.connection.runtime.organizationId),
      httpBaseUrl: input.connection.httpBaseUrl,
      wsBaseUrl: input.connection.wsBaseUrl,
      runtimeTicketExpiresAt: input.connection.expiresAt,
      ...(resolvedEnvironmentId !== input.environmentId
        ? { replacesEnvironmentId: input.environmentId }
        : {}),
    },
    bearerToken: input.connection.bearerToken,
    ...(input.signal ? { signal: input.signal } : {}),
  });
}

const DEFAULT_EXECUTION_INTENT_DEPS: ThreadExecutionIntentEnvironmentApiDeps = {
  ...DEFAULT_DEPS,
  prepareRuntimeExecutionIntent,
  recoverCheckoutRegistration: (environmentId, threadId) =>
    readThreadCmmdProjectId(scopeThreadRef(environmentId, ThreadId.make(threadId))),
  registerRuntimeExecutionConnection,
};

async function ensureDesktopExecutionEnvironmentApi(
  environmentId: EnvironmentId,
  deps: ThreadActionEnvironmentApiDeps,
): Promise<EnvironmentApi> {
  const existing = deps.readApi(environmentId);
  if (existing) return existing;
  const connected = await deps.connectDesktopEnvironment({
    environmentId,
    primaryEnvironmentId: deps.readPrimaryEnvironmentId(),
    label: deps.readEnvironmentLabel(environmentId),
  });
  if (connected) return connected;

  const registered = deps.readApi(environmentId);
  if (!registered) throw new Error(THREAD_RUNTIME_UNAVAILABLE_MESSAGE);
  return registered;
}

function resolveExecutionIntentRuntimeId(input: {
  readonly environmentId: EnvironmentId;
  readonly runtimeId: string | undefined;
}): string {
  const catalogRuntimeId = findCloudCatalogRow(input.environmentId)?.row.runtimeId;
  const resolvedRuntimeId = input.runtimeId ?? catalogRuntimeId;
  if (!resolvedRuntimeId) throw new Error(THREAD_RUNTIME_UNAVAILABLE_MESSAGE);
  return resolvedRuntimeId;
}

async function readPreparedCloudEnvironmentApi(input: {
  readonly environmentId: EnvironmentId;
  readonly cloudEnvironmentId: EnvironmentId;
  readonly deps: ThreadExecutionIntentEnvironmentApiDeps;
}): Promise<EnvironmentApi | null> {
  const existing =
    input.deps.readApi(input.cloudEnvironmentId) ?? input.deps.readApi(input.environmentId);
  if (existing) return existing;
  return null;
}

/** Only an Environment-scoped wake may go ahead without naming a Checkout. */
function requireCheckoutRegistration(
  environmentId: EnvironmentId,
  intent: {
    readonly projectId: number | null;
    readonly action: RuntimeExecutionIntentAction;
    readonly targetId: string | null;
  },
  deps: ThreadExecutionIntentEnvironmentApiDeps,
): number | null {
  if (intent.projectId !== null) return intent.projectId;
  const recovered = intent.targetId
    ? deps.recoverCheckoutRegistration(environmentId, intent.targetId)
    : null;
  if (recovered === null && !(intent.action === "wake" && intent.targetId === null)) {
    throw new Error(MISSING_CHECKOUT_REGISTRATION_MESSAGE);
  }
  return recovered;
}

export async function prepareThreadExecutionIntentEnvironmentApi(
  environmentId: EnvironmentId,
  intent: {
    readonly runtimeId?: string;
    readonly projectId: number | null;
    readonly action: RuntimeExecutionIntentAction;
    readonly targetId: string | null;
    readonly onWaiting?: RuntimeExecutionIntentWaitHandler;
    readonly dispatchIdentity: CommandId;
    readonly signal?: AbortSignal;
  },
  deps: ThreadExecutionIntentEnvironmentApiDeps = DEFAULT_EXECUTION_INTENT_DEPS,
): Promise<{
  readonly api: EnvironmentApi | null;
  readonly intent:
    | (RuntimeExecutionIntentData["intent"] & {
        readonly status: "ready" | "dispatched";
      })
    | null;
}> {
  const durableEnvironmentId = deps.resolveCloudEnvironmentId(environmentId);
  const isCloudEnvironment =
    deps.isCloudEnvironment(environmentId) || durableEnvironmentId !== null;
  if (!isCloudEnvironment) {
    return {
      api: await ensureDesktopExecutionEnvironmentApi(environmentId, deps),
      intent: null,
    };
  }

  const lifecycleState = deps.readCloudLifecycleState(environmentId);
  if (lifecycleState === "paused") throw new Error(PAUSED_ENVIRONMENT_MESSAGE);
  if (lifecycleState === "unavailable") throw new Error(UNAVAILABLE_ENVIRONMENT_MESSAGE);
  const projectId = requireCheckoutRegistration(environmentId, intent, deps);

  const cloudEnvironmentId = durableEnvironmentId ?? environmentId;
  const runtimeId = resolveExecutionIntentRuntimeId({
    environmentId,
    runtimeId: intent.runtimeId,
  });
  const prepared = await deps.prepareRuntimeExecutionIntent({
    ...intent,
    projectId,
    runtimeId,
    environmentId: cloudEnvironmentId,
  });
  if (prepared.intent.status !== "ready" && prepared.intent.status !== "dispatched") {
    throw new Error("This Environment action did not reach a dispatchable state.");
  }
  const preparedIntent = { ...prepared.intent, status: prepared.intent.status };
  if (prepared.connection) {
    await deps.registerRuntimeExecutionConnection({
      environmentId: cloudEnvironmentId,
      connection: prepared.connection,
      ...(intent.signal ? { signal: intent.signal } : {}),
    });
  }
  const api = await readPreparedCloudEnvironmentApi({
    environmentId,
    cloudEnvironmentId,
    deps,
  });
  if (preparedIntent.status === "ready" && api === null) {
    throw new Error(THREAD_RUNTIME_UNAVAILABLE_MESSAGE);
  }
  return { api, intent: preparedIntent };
}
